import React from "react";
// API utility and constants for PaymentApproval
import {
  fetchAllPostPromisedData,
  fetchAllPromisedData,
} from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { endpoint } from "views/pages/manage/common/constant";

import { getDownloadFileUrl } from "views/pages/manage/edpOps/constant/common";
import CustomChip from "views/pages/manage/common/customChip/CustomChip";
import { generateProgressSteps } from "modules/studentView/constant";

import { formatDateTimeForApi } from "../helper";

export const APPROVAL_STATUS_ENUM = {
  SUBMITTED: "Awaiting L1 Approval",
  L1_APPROVED: "L1 APPROVED",
  L2_APPROVED: "L2 APPROVED",
  L3_APPROVED: "L3 APPROVED",
  L4_APPROVED: "L4 APPROVED",
  PROCESSED: "PROCESSED",
  L1_REJECTED: "L1 REJECTED",
  L2_REJECTED: "L2 REJECTED",
  L3_REJECTED: "L3 REJECTED",
  L4_REJECTED: "L4 REJECTED",
};

export const PAYMENT_APPROVAL_STATUS_OPTIONS = [
  { label: "Submitted", value: "SUBMITTED" },
  { label: "L1 Approved", value: "L1_APPROVED" },
  { label: "L2 Approved", value: "L2_APPROVED" },
  { label: "L3 Approved", value: "L3_APPROVED" },
  { label: "L4 Approved", value: "L4_APPROVED" },
  { label: "Processed", value: "PROCESSED" },
  { label: "L1 Rejected", value: "L1_REJECTED" },
  { label: "L2 Rejected", value: "L2_REJECTED" },
  { label: "L3 Rejected", value: "L3_REJECTED" },
  { label: "L4 Rejected", value: "L4_REJECTED" },
  { label: "Failed", value: "SAP_SYNC_FAILED" },
];

// Maps approvalStatus to approval config LEVEL_X for action logic
export const approvalLevelMap = {
  SUBMITTED: "L1_APPROVED",
  L1_APPROVED: "L2_APPROVED",
  L2_APPROVED: "L3_APPROVED",
  L3_APPROVED: "L4_APPROVED",
};

export const rejectionLevelMap = {
  SUBMITTED: "L1_REJECTED",
  L1_APPROVED: "L2_REJECTED",
  L2_APPROVED: "L3_REJECTED",
  L3_APPROVED: "L4_REJECTED",
};

export const actionableApproverLevelMap = {
  SUBMITTED: "LEVEL_1",
  L1_APPROVED: "LEVEL_2",
  L2_APPROVED: "LEVEL_3",
  L3_APPROVED: "LEVEL_4",
};

// Fetch vendorLevels for userRole
export const fetchVendorLevelsByRole = async (roleId) => {
  const url = `${endpoint.vendorApprovalRequests.getVendorsByRoleId}/${roleId}`;
  return await fetchAllPromisedData(url);
};

// Logic to enable/disable Approve/Reject buttons
// vendorLevelsApi should be the full API response.data object
export const isActionEnabledForVendorRow = (row, userRole, vendorLevelsApi) => {
  // Final/terminal statuses
  if (
    !row.approvalStatus ||
    row.approvalStatus === "PROCESSED" ||
    row.approvalStatus === "L4_REJECTED" ||
    row.approvalStatus.endsWith("_REJECTED")
  ) {
    return false;
  }

  // Map status to required approverLevel
  const statusLevelMap = {
    SUBMITTED: 1,
    L1_APPROVED: 2,
    L2_APPROVED: 3,
    L3_APPROVED: 4,
  };
  const currentLevel = statusLevelMap[row.approvalStatus];
  if (!currentLevel) return false;

  // userRole must match API response data.roleDispVal
  const { roleDispVal = "", vendorLevels = [] } = vendorLevelsApi || {};
  if (!roleDispVal || !Array.isArray(vendorLevels)) return false;
  if (userRole !== roleDispVal) return false;

  // vendorLevels: array of vendorLevel objects
  return vendorLevels.some((vl) => {
    const buMatch = vl.buCode === row.businessAreaDispValue;
    const yearMatch = vl.year === row.academicYearDispValue;
    const vendorTypeMatch = vl.vendorType === row.vendorType;
    // Check if approverLevels array contains currentLevel
    const approverLevelMatch = vl.approverLevels.some(
      (al) => al.approverLevel === currentLevel,
    );

    return vendorTypeMatch && buMatch && yearMatch && approverLevelMatch;
  });
};

export const FILTERS_DEFAULT_STATE = {
  approvalDateFrom: "",
  approvalDateTo: "",
  vendorType: "",
  bu: [],
  plantId: [],
  status: [],
};

export async function fetchPaymentApprovalList({
  searchBy,
  searchByValue,
  filters,
  offset = 0,
  limit = 50,
  businessAreaIds,
}) {
  const url = `${endpoint.vendorApprovalRequests.paymentApprovalList}?offset=${offset}&limit=${limit}`;

  const payload = buildVendorSearchPayload(
    { ...filters, searchBy, searchByValue, businessAreaIds } || {},
  );
  const response = await fetchAllPostPromisedData(url, payload, "post");

  return response;
}

export const buildVendorSearchPayload = (filters) => {
  const payload = {
    vendorType: filters.vendorType ? [filters.vendorType] : undefined,
    businessAreaIds: filters.bu.length ? filters.bu : filters.businessAreaIds,
    sapPlantId: filters.plantId || undefined,
    transactionStatus: filters.status || undefined,
  };

  const approvalFromDate = formatDateTimeForApi(filters.approvalDateFrom);
  const approvalToDate = formatDateTimeForApi(filters.approvalDateTo, "end");

  if (approvalFromDate) payload.approvalFromDate = approvalFromDate;
  if (approvalToDate) payload.approvalToDate = approvalToDate;

  const searchBy = filters.searchBy;
  const searchByValue = filters.searchByValue;
  if (searchBy && searchByValue) {
    payload.searchBy = searchBy;
    payload.searchByValue = searchByValue;
  }

  return payload;
};

export const downloadVendorsRequestsCsv = async ({
  searchBy,
  searchByValue,
  filters,
}) => {
  const payload = buildVendorSearchPayload(
    { ...filters, searchBy, searchByValue } || {},
  );
  const response = await fetchAllPostPromisedData(
    `${endpoint.vendorApprovalRequests.downloadCsv}?offset=0&limit=2000`,
    payload,
    "post",
  );

  // expected: response.data => s3 key
  if (response?.code === 200 && response?.data) {
    await getDownloadFileUrl(response.data);
  }

  return response;
};

export const editTransactionStatus = async (transactionId, payload) => {
  const response = await fetchAllPostPromisedData(
    `${endpoint.vendorApprovalRequests.setTransactionStatus}/${transactionId}`,
    payload,
    "post",
  );

  return response;
};

export const fetchApprovalJourney = async (id, limit = 10, offset = 0) => {
  return await fetchAllPromisedData(
    `${endpoint.vendorManagement.auditLogs}?limit=${limit}&offset=${offset}&item_id=${id}&eventType=VENDOR_TRANSACTION_APPROVAL`,
  );
};

export const STATUS_BADGE = (status) => {
  const statusBadgeMap = {
    SUBMITTED: "Initiated",
    L1_APPROVED: "L1 Approved",
    L1_REJECTED: "L1 Rejected",
    L2_APPROVED: "L2 Approved",
    L2_REJECTED: "L2 Rejected",
    L3_APPROVED: "L3 Approved",
    L3_REJECTED: "L3 Rejected",
    L4_APPROVED: "L4 Approved",
    L4_REJECTED: "L4 Rejected",
    PROCESSED: "Processed",
    SAP_SYNC_FAILED: "SAP Sync Failed",
  };

  const getInfo = () => {
    if (
      status.includes("APPROVED") ||
      status.includes("SUBMITTED") ||
      status.includes("PROCESSED")
    )
      return {
        text: statusBadgeMap[status],
        textColor: "#0B442D",
        bgColor: "#BBE2CE",
      };
    else if (status.includes("PENDING")) {
      const level = status.split("_")[0];
      return {
        text: `Pending at ${level}`,
        textColor: "#705010",
        bgColor: "#FFEDC8",
      };
    } else if (status.includes("REJECTED") || status.includes("FAILED"))
      return {
        text: statusBadgeMap[status],
        textColor: "#B32306",
        bgColor: "#F9D9D7",
      };
  };

  return <CustomChip {...getInfo()} />;
};

export const getApproverLevelFromLogs = (logs, defaultLevel = 4) => {
  if (!logs?.length) return defaultLevel;

  const processedIndex = logs.findIndex((log) => log.event === "PROCESSED");

  if (processedIndex !== -1) {
    const prevEvent = logs[processedIndex + 1]?.event;
    const match = prevEvent?.match(/L(\d+)_APPROVED/);
    if (match) {
      return Number(match[1]);
    }
  }

  return defaultLevel;
};

// Custom: Returns the dynamic steps for payment approval timeline (matches design)
// [Request initiated, L1 Approval, L2 Approval, L3 Approval, L4 Approval, Payment Processed]
export const getProgressStepList = (approverLevel = 4) => {
  const maxLevel = Math.min(approverLevel, 4);

  const steps = [{ title: "Request initiated" }];

  for (let i = 1; i <= maxLevel; i++) {
    steps.push({ title: `L${i} Approval` });
  }

  steps.push({ title: "Payment Processed" });

  return steps;
};
// Pure function: Returns formatted stepper steps for payment approval timeline given logs
export const getPaymentApprovalStepperSteps = (
  logs = [],
  approverLevel = 4,
) => {
  const calculatedLevel = getApproverLevelFromLogs(logs, approverLevel);

  const eventTitleMap = {
    SUBMITTED: "Request initiated",
    L1_APPROVED: "L1 Approval",
    L1_REJECTED: "L1 Approval",
    L2_APPROVED: "L2 Approval",
    L2_REJECTED: "L2 Approval",
    L3_APPROVED: "L3 Approval",
    L3_REJECTED: "L3 Approval",
    L4_APPROVED: "L4 Approval",
    L4_REJECTED: "L4 Approval",
    PROCESSED: "Payment Processed",
    SAP_SYNC_FAILED: "Payment Processed",
  };

  const progressSteps = getProgressStepList(calculatedLevel);

  const normalizedLogs = logs
    .filter((log) => eventTitleMap[log.event])
    .map((log) => {
      let status = "completed";

      if (log.event.includes("REJECTED"))
        status = "rejected";
      else if(log.event.includes("FAILED"))
        status = "failed";
      else if (log.event === "PROCESSED") status = "success";
      else if (log.event.includes("APPROVED")) status = "approved";
      else if (log.event === "SUBMITTED") status = "completed";

      return {
        title: eventTitleMap[log.event],
        status,
        updatedOn: log.updatedOn || log.createdOn,
        updatedBy: log.updatedBy || log.createdBy,
        remark: log.newValue || "",
      };
    });

  const generatedSteps = generateProgressSteps(progressSteps, normalizedLogs);

  const lastCompletedIndex = generatedSteps.findLastIndex(
    (step) =>
      step.status === "completed" ||
      step.status === "approved" ||
      step.status === "success",
  );

  if (
    lastCompletedIndex !== -1 &&
    lastCompletedIndex < generatedSteps.length - 1
  ) {
    const nextStep = generatedSteps[lastCompletedIndex + 1];

    if (nextStep.status === "upcoming") {
      generatedSteps[lastCompletedIndex + 1] = {
        ...nextStep,
        status: "in_process",
      };
    }
  }

  return generatedSteps;
};

export const FIND_SUCCESS_MESSAGES = (userId, status, remark) => {
  switch (status) {
    case "L1_APPROVED":
      return `Payment request approved at Level 1 by ${userId}.`;
    case "L2_APPROVED":
      return `Payment request approved at Level 2 by ${userId}.`;
    case "L3_APPROVED":
      return `Payment request approved at Level 3 by ${userId}.`;
    case "L4_APPROVED":
      return `Payment request approved at Level 4 by ${userId}.`;
    case "PROCESSED":
      return `Payment request approved at Final Level by ${userId}.`;
    case "L1_REJECTED":
      return `Payment request rejected at Level 1 by ${userId} due to remark: ${remark}.`;
    case "L2_REJECTED":
      return `Payment request rejected at Level 2 by ${userId} due to remark: ${remark}.`;
    case "L3_REJECTED":
      return `Payment request rejected at Level 3 by ${userId} due to remark: ${remark}.`;
    case "L4_REJECTED":
      return `Payment request rejected at Final Level by ${userId} due do remark: ${remark}.`;
  }
};

export const fetchPaymentOrderInfo = async (id) =>
  await fetchAllPromisedData(
    `${endpoint.vendorApprovalRequests.getPaymentOrderInfo}/${id}`,
  );

export const fetchVendorApprovalConfigs = async (payload) =>
  await fetchAllPostPromisedData(
    `${endpoint.vendorConfig.search}?page=0&size=10`,
    payload,
    "post",
  );

export const isL4Approver = (roleLevelApprovals, userRole) => {
  // roleLevelApprovals: { LEVEL_1: [...], LEVEL_2: [...], LEVEL_3: [...], LEVEL_4: [...] }
  // userRole: string
  return Object.values(roleLevelApprovals || {}).some((arr) =>
    arr.some((r) => r.roleDispVal === userRole),
  );
};

// Returns the next approver role for a given status
export const getNextApproverRole = (status) => {
  const statusRoleMap = {
    SUBMITTED: "LEVEL_1",
    L1_APPROVED: "LEVEL_2",
    L2_APPROVED: "LEVEL_3",
    L3_APPROVED: "LEVEL_4",
  };
  return statusRoleMap[status] || null;
};

// Checks if user has access for a given LEVEL_X in approvalConfigs
export const hasApproverAccess = (approvalConfigs, userRole, level) => {
  if (!approvalConfigs || !approvalConfigs.length) return false;
  // if (userRole !== "VNDR_CORPORATE") return false;
  const roleLevelApprovals = approvalConfigs[0]?.roleLevelApprovals || {};
  const approvers = roleLevelApprovals[level] || [];
  return approvers.some((r) => r.roleDispVal === userRole);
};

// Determines if action buttons should be enabled for the current status and userRole
export const isActionEnabledForApproval = (
  status,
  userRole,
  approvalConfigs,
) => {
  // Terminal statuses
  if (
    !status ||
    status === "PROCESSED" ||
    status === "L4_REJECTED" ||
    status.endsWith("_REJECTED")
  ) {
    return false;
  }
  // Get next approver level
  const nextLevel = getNextApproverRole(status);
  if (!nextLevel) return false;
  // Check if user has access for nextLevel
  return hasApproverAccess(approvalConfigs, userRole, nextLevel);
};

// Returns the status to send for approve/reject
export const getApprovalActionStatus = (
  currentStatus,
  action,
  userRole,
  approvalConfigs,
) => {
  // action: 'approve' | 'reject'
  const nextLevel = getNextApproverRole(currentStatus);
  if (!nextLevel) return null;
  // Find user's role in nextLevel
  const roleLevelApprovals = approvalConfigs[0]?.roleLevelApprovals || {};
  const approvers = roleLevelApprovals[nextLevel] || [];
  const userApprover = approvers.find((r) => r.roleDispVal === userRole);
  if (!userApprover) return null;
  // Compose status
  if (action === "approve") {
    return `${nextLevel.replace("LEVEL_", "L")}_APPROVED`;
  } else if (action === "reject") {
    return `${nextLevel.replace("LEVEL_", "L")}_REJECTED`;
  }
  return null;
};

export const fetchApprovalLevelDetails = async (req, page = 0, size = 100) =>
  await fetchAllPostPromisedData(
    `${endpoint.vendorConfig.searchVendorApprovalConfigs}?page=${page}&size=${size}`,
    req,
  );

export const getMaxApproverLevel = (data = {}, auditList) => {
  if (auditList?.length && auditList?.[0]?.event.includes("REJECTED")) {
    const auditEvent = auditList?.[0]?.event;
    const match = auditEvent?.match(/L(\d+)_/);
    if (match) return Math.max(Number(match[1]));
  }
  let maxLevel = 0;

  const approvals = data?.roleLevelApprovals;
  if (!approvals) return 0;

  for (const levelArr of Object.values(approvals)) {
    if (!Array.isArray(levelArr)) continue;

    for (const role of levelArr) {
      const level = role?.approverLevel ?? 0;
      if (level > maxLevel) {
        maxLevel = level;
      }
    }
  }
  if (auditList?.[0]?.event.includes("APPROVED")) {
    const auditEvent = auditList?.[0]?.event;
    const match = auditEvent?.match(/L(\d+)_/);
    if (match) return Math.max(Number(match[1]), maxLevel);
  }
  return maxLevel;
};
